import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CognitoUserPool, CognitoUser } from 'amazon-cognito-identity-js';
import './ForgotPassword.css';
import logoImage from '../../assets/icon_logo.png';

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
};

const userPool = new CognitoUserPool(poolData);

function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' or 'reset'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const passwordRequirements = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[^A-Za-z0-9]/.test(newPassword),
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword;

  const handleSendCode = (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.forgotPassword({
      onSuccess: () => {
        console.log('Password reset code sent');
        setStep('reset');
        setLoading(false);
      },
      onFailure: (err) => {
        console.error('Failed to send code:', err);
        setError(err.message || 'Failed to send reset code');
        setLoading(false);
      },
    });
  };

  const handleResetPassword = (e) => {
    e.preventDefault();

    if (!isPasswordValid) {
      setError('Password does not meet all requirements');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError(null);

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => {
        console.log('Password reset successful');
        alert('Password reset successful! Please login with your new password.');
        navigate('/login');
      },
      onFailure: (err) => {
        console.error('Password reset failed:', err);
        setError(err.message || 'Password reset failed');
        setLoading(false);
      },
    });
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src={logoImage} alt="CoinedIn Logo" className="auth-logo" />
          <h1>CoinedIn</h1>
          <p className="auth-tagline">Reset Your Password</p>
        </div>

        {step === 'email' ? (
          <form onSubmit={handleSendCode} className="auth-form">
            <h2>Forgot Password</h2>
            <p className="reset-description">
              Enter your email address and we'll send you a verification code to reset your password.
            </p>

            {error && (
              <div className="auth-error">
                <span className="error-icon">⚠</span>
                <p>{error}</p>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>

            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>

            <div className="auth-links">
              <button
                type="button"
                className="link-button"
                onClick={() => navigate('/login')}
                disabled={loading}
              >
                Back to login
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="auth-form">
            <h2>Reset Password</h2>
            <p className="reset-description">
              Enter the verification code sent to <strong>{email}</strong> and your new password.
            </p>

            {error && (
              <div className="auth-error">
                <span className="error-icon">⚠</span>
                <p>{error}</p>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="code">Verification Code</label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter 6-digit code"
                required
                disabled={loading}
                maxLength={6}
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                disabled={loading}
                autoComplete="new-password"
              />
            </div>

            {newPassword && (
              <div className="password-requirements">
                <h4>Password Requirements:</h4>
                <ul>
                  <li className={passwordRequirements.length ? 'met' : ''}>
                    At least 8 characters
                  </li>
                  <li className={passwordRequirements.uppercase ? 'met' : ''}>
                    One uppercase letter
                  </li>
                  <li className={passwordRequirements.lowercase ? 'met' : ''}>
                    One lowercase letter
                  </li>
                  <li className={passwordRequirements.number ? 'met' : ''}>
                    One number
                  </li>
                  <li className={passwordRequirements.special ? 'met' : ''}>
                    One special character
                  </li>
                </ul>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                required
                disabled={loading}
                autoComplete="new-password"
              />
              {confirmPassword && !passwordsMatch && (
                <small className="error-text">Passwords do not match</small>
              )}
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={loading || !isPasswordValid || !passwordsMatch}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>

            <div className="auth-links">
              <button
                type="button"
                className="link-button"
                onClick={() => setStep('email')}
                disabled={loading}
              >
                Resend code
              </button>
              <span className="separator">•</span>
              <button
                type="button"
                className="link-button"
                onClick={() => navigate('/login')}
                disabled={loading}
              >
                Back to login
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword;
